// Detección de cruces (precio×media, media×media) para ema_sma_quantum.
//
// Función pura y portable: cero dependencias de UI, React, red o storage.
// Cuando llegue la Fase 1 (evaluación real en backend + envío por Telegram —
// ver docs/indicators/ema_sma_quantum.md), el evaluador del backend debe
// replicar EXACTAMENTE esta misma semántica (cierre de vela, sin
// retroactivos, dedup por candleTime) para que un cruce se vea y se
// notifique igual por los dos canales. No dupliques esta lógica allí: pórtala.
//
// Semántica de "cruce" (evaluada solo con precios/valores DE CIERRE, nunca
// mechas): A cruza al alza sobre B si en la vela anterior A NO estaba por
// encima de B (prevA <= prevB) y en esta vela A SÍ lo está (currA > currB).
// Simétrico a la baja. Un simple "toque" (curr === other) no cuenta como
// cruce en ninguna dirección — ni prevA<=prevB&&currA>currB ni su espejo se
// cumplen cuando currA === currB.
export function detectCross(prevA, currA, prevB, currB) {
  if (prevA == null || currA == null || prevB == null || currB == null) return null
  if (prevA <= prevB && currA > currB) return 'up'
  if (prevA >= prevB && currA < currB) return 'down'
  return null
}

export function maLabel(ma) {
  return `${String(ma.type).toUpperCase()} ${ma.period}`
}

// Construye las vigilancias vigentes a partir de las medias ACTIVAS del
// usuario: una vigilancia "precio×media" por cada media activa, y una
// vigilancia "media×media" por cada PAR de medias activas (todas las
// combinaciones, sin importar tipo). Determinista y dinámico: activar o
// desactivar una media cambia el resultado en la siguiente llamada, sin
// estado propio que arrastrar.
export function buildWatches(activeMAs) {
  const mas = Array.isArray(activeMAs) ? activeMAs.filter(m => m && m.active) : []
  const watches = mas.map(ma => ({
    key: `price:${ma.id}`,
    kind: 'price_ma',
    maId: ma.id,
    maLabel: maLabel(ma),
  }))
  for (let i = 0; i < mas.length; i++) {
    for (let j = i + 1; j < mas.length; j++) {
      const a = mas[i]
      const b = mas[j]
      // La de MENOR periodo es "A" (cruce al alza de A sobre B = golden
      // cross). Con periodos iguales (p.ej. EMA 16 y SMA 16) no hay un
      // "menor" real — se ordena por id para que la clave del watch sea
      // estable entre renders; la dirección sigue siendo un cruce válido,
      // solo cambia cuál de las dos se llama "A" en el mensaje.
      const [lo, hi] = a.period !== b.period
        ? (a.period < b.period ? [a, b] : [b, a])
        : (a.id < b.id ? [a, b] : [b, a])
      watches.push({
        key: `pair:${lo.id}:${hi.id}`,
        kind: 'ma_ma',
        smallerId: lo.id,
        largerId: hi.id,
        smallerLabel: maLabel(lo),
        largerLabel: maLabel(hi),
      })
    }
  }
  return watches
}

export function formatEventMessage(event) {
  if (event.kind === 'price_ma') {
    return event.direction === 'up'
      ? `Precio cruza al alza ${event.maLabel}`
      : `Precio cruza a la baja ${event.maLabel}`
  }
  return event.direction === 'up'
    ? `Cruce alcista: ${event.smallerLabel} × ${event.largerLabel}`
    : `Cruce bajista: ${event.smallerLabel} × ${event.largerLabel}`
}

// bars: velas YA CERRADAS (nunca la vela en formación), orden ascendente por
// time: [{ time, price, values: {[maId]: number|null} }].
// watches: de buildWatches().
// fromTime: cursor de dedup — se ignoran transiciones cuya vela CURR tenga
// time <= fromTime. Esto da dedup (repetir la misma llamada con el mismo
// fromTime no vuelve a emitir nada) Y evita retroactivos (al activar el
// toggle, el llamador debe fijar fromTime = time de la última vela cerrada
// EN ESE MOMENTO, así el primer cruce evaluado es estrictamente futuro).
export function evaluateBars(bars, watches, fromTime) {
  const events = []
  const baseline = fromTime ?? -Infinity
  let lastTime = bars.length > 0 ? bars[bars.length - 1].time : baseline
  for (let i = 1; i < bars.length; i++) {
    const prev = bars[i - 1]
    const curr = bars[i]
    if (curr.time <= baseline) continue
    for (const w of watches) {
      const [prevA, currA, prevB, currB] = w.kind === 'price_ma'
        ? [prev.price, curr.price, prev.values[w.maId], curr.values[w.maId]]
        : [prev.values[w.smallerId], curr.values[w.smallerId], prev.values[w.largerId], curr.values[w.largerId]]
      const direction = detectCross(prevA, currA, prevB, currB)
      if (!direction) continue
      const base = { watchKey: w.key, kind: w.kind, direction, time: curr.time }
      events.push(w.kind === 'price_ma'
        ? { ...base, maLabel: w.maLabel }
        : { ...base, smallerLabel: w.smallerLabel, largerLabel: w.largerLabel })
    }
  }
  return { events, lastTime }
}
